import type { FC } from 'react';
import {
    Box,
    Button,
    Container,
    Typography,
    Card,
    Stack
} from '@mui/material';

import PublicIcon from '@mui/icons-material/Public';
import PaymentIcon from '@mui/icons-material/Payment';
import TokenIcon from '@mui/icons-material/Token';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

interface StatItemProps {
    label: string;
    value: string;
    icon: React.ReactNode;
}

const StatItem: FC<StatItemProps> = ({ label, value, icon }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: { xs: 1.5, md: 2 },
            width: { xs: '100%', sm: 'auto' }
        }}
    >
        <Box
            sx={{
                color: '#FF6B00',
                mb: 0.5,
                opacity: 0.9
            }}
        >
            {icon}
        </Box>
        <Typography
            variant="h3"
            sx={{
                color: 'common.white',
                fontWeight: 700,
                mb: 0.5,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
            }}
        >
            {value}
        </Typography>
        <Typography
            variant="body2"
            sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                textAlign: 'center',
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
            }}
        >
            {label}
        </Typography>
    </Box>
);

export const HomeStats: FC = () => {
    return (
        <Box
            component="section"
            sx={{
                backgroundColor: '#0F1521',
                color: 'common.white',
                position: 'relative',
                overflow: 'hidden',
                py: { xs: 6, md: 10 },
                minHeight: { xs: 'auto', md: '100vh' },
                display: 'flex',
                alignItems: 'center'
            }}
        >
            {/* Orange accent elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '15%',
                    left: '10%',
                    width: '2px',
                    height: '100px',
                    backgroundColor: '#FF6B00',
                    opacity: 0.3,
                    zIndex: 0
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '20%',
                    left: '20%',
                    width: '100px',
                    height: '2px',
                    backgroundColor: '#FF6B00',
                    opacity: 0.3,
                    zIndex: 0
                }}
            />

            {/* Globe image */}
            <Box
                component="img"
                src="/globe.avif"
                alt="Globe"
                sx={{
                    position: 'absolute',
                    bottom: { xs: '-5%', md: '-15%' },
                    right: { xs: '-20%', md: '0%' },
                    width: { xs: '120%', md: '70%' },
                    maxWidth: '1000px',
                    opacity: 0.8,
                    zIndex: 0,
                    filter: 'hue-rotate(30deg) saturate(0.8)'
                }}
            />

            <Container
                maxWidth="lg"
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    mt: { xs: 0, md: -10 }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        maxWidth: '700px',
                        mx: 'auto',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            color: 'common.white',
                            fontWeight: 700,
                            mb: { xs: 3, md: 4 },
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                            lineHeight: 1.3
                        }}
                    >
                        Empowering businesses worldwide to <Typography
                            component="span"
                            color="#FF6B00"
                            variant="inherit"
                            sx={{ fontWeight: 700 }}
                        >
                            embrace crypto
                        </Typography>
                    </Typography>

                    <Card
                        sx={{
                            backgroundColor: 'rgba(30, 37, 50, 0.8)',
                            backdropFilter: 'blur(8px)',
                            borderRadius: 3,
                            py: { xs: 2, md: 2.5 },
                            px: { xs: 1.5, md: 2 },
                            mb: { xs: 3, md: 4 },
                            width: { xs: '100%', md: '85%' },
                            border: '1px solid rgba(255, 107, 0, 0.2)'
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={0}
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                                '& > *:not(:last-child)': {
                                    borderBottom: { xs: '1px solid rgba(255, 107, 0, 0.1)', sm: 'none' },
                                    borderRight: { xs: 'none', sm: '1px solid rgba(255, 107, 0, 0.1)' },
                                    pb: { xs: 2, sm: 0 },
                                    mb: { xs: 2, sm: 0 },
                                    pr: { xs: 0, sm: 1 }
                                }
                            }}
                        >
                            <StatItem
                                label="Countries"
                                value="30+"
                                icon={<PublicIcon sx={{ fontSize: 22 }} />}
                            />
                            <StatItem
                                label="Payment Methods"
                                value="10+"
                                icon={<PaymentIcon sx={{ fontSize: 22 }} />}
                            />
                            <StatItem
                                label="Tokens"
                                value="400+"
                                icon={<TokenIcon sx={{ fontSize: 22 }} />}
                            />
                            <StatItem
                                label="Blockchains"
                                value="20+"
                                icon={<AccountBalanceIcon sx={{ fontSize: 22 }} />}
                            />
                        </Stack>
                    </Card>

                    <Button
                        component="a"
                        href="https://cal.com/support-team-qsszsx"
                        target="_blank"
                        variant="contained"
                        size="large"
                        sx={{
                            py: 1.25,
                            px: 4,
                            borderRadius: 2,
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            backgroundColor: '#FF6B00',
                            '&:hover': {
                                backgroundColor: '#CC5500',
                            },
                            boxShadow: '0 4px 14px 0 rgba(255, 107, 0, 0.4)'
                        }}
                    >
                        Talk to us
                    </Button>
                </Box>
            </Container>

            {/* Additional dot pattern overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: 'radial-gradient(circle, rgba(255, 107, 0, 0.03) 1px, transparent 1px)',
                    backgroundSize: '20px 20px',
                    opacity: 0.2,
                    zIndex: 0
                }}
            />
        </Box>
    );
}; 